import { useEffect, useRef, useState } from 'react';

import { useProject } from '@contexts/Project.context';
import { setupFreehandDraw, removeFreehandDraw } from '@utils/map/freeHand.draw';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';

const DEFAULT_LAYERS_TO_HIDE = ['hexagonLayer'];

/**
 * Shared "draw a polygon on the map" flow used by the detection features
 * (the legacy Detect-in-area button and the Object Picker's Area step).
 *
 * Handles the common lifecycle both call sites need: prep the map (hide the
 * toolbar, disable map interactions, temporarily hide distracting layers, show
 * a prompt dialogue), run an OpenLayers freehand polygon draw, hand the drawn
 * outer ring to `onComplete`, then tear the interaction down and restore the
 * map. ESC cancels and calls `onCancel`. Only the completion action differs
 * between callers (run detection vs. store the area); everything else is shared.
 *
 * The map + disabled state stay in place until `onComplete` resolves, so a
 * caller can await a confirm dialog while the drawn polygon is still visible.
 *
 * Must be used from a component that stays mounted for the whole draw (the map
 * shell / a modal shell), not one that unmounts while drawing.
 *
 * @param {object} opts
 * @param {{ header: string, body: React.ReactNode }} opts.dialogue - drawing prompt
 * @param {string[]} [opts.layersToHide] - customLayerIds hidden while drawing
 * @param {(ring: Array, feature: object) => void | Promise<void>} [opts.onComplete]
 * @param {() => void} [opts.onStart]  - fired right before drawing begins
 * @param {() => void} [opts.onCancel] - fired when the user presses ESC
 * @returns {{ isDrawing: boolean, startDraw: () => void, cancelDraw: () => void }}
 */
export const useAreaDraw = ({
	dialogue,
	layersToHide = DEFAULT_LAYERS_TO_HIDE,
	onComplete,
	onStart,
	onCancel,
} = {}) => {
	const { mapObject, dispatch } = useProject();

	const [isDrawing, setIsDrawing] = useState(false);
	const drawRef = useRef(null);
	// customLayerIds that were visible before drawing, so we restore exactly those.
	const restoreLayersRef = useRef([]);
	// Latest callbacks, read inside the draw effect without re-running it.
	const handlersRef = useRef({});
	handlersRef.current = { onComplete, onCancel };

	const getVisibleLayerIds = () => {
		const layers = mapObject?.getLayers()?.getArray();
		if (!layers?.length) return [];
		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const teardownDraw = () => {
		const { draw, layer } = drawRef.current ?? {};
		if (draw || layer) removeFreehandDraw({ mapRef: mapObject, draw, layer });
		drawRef.current = null;
	};

	const restoreMap = () => {
		dispatch({ type: 'setDialogue', payload: null });
		dispatch({ type: 'setToolBarVisible', payload: true });
		dispatch({ type: 'setInteractionsDisabled', payload: false });
		setLayersVisibilityByCustomIds(mapObject, restoreLayersRef.current, true);
	};

	// Enter draw mode: prep the map and start a polygon draw.
	const startDraw = () => {
		if (!mapObject) return;

		onStart?.();

		dispatch({
			type: 'setDialogue',
			payload: {
				header: dialogue?.header,
				body: dialogue?.body,
				fullWidth: false,
				autoClose: true,
			},
		});
		dispatch({ type: 'setToolBarVisible', payload: false });
		dispatch({ type: 'setInteractionsDisabled', payload: true });

		restoreLayersRef.current = getVisibleLayerIds();
		setLayersVisibilityByCustomIds(mapObject, layersToHide, false);

		setIsDrawing(true);
	};

	const cancelDraw = () => {
		teardownDraw();
		restoreMap();
		setIsDrawing(false);
		handlersRef.current.onCancel?.();
	};

	// Set up the actual OL draw interaction whenever we enter drawing mode.
	useEffect(() => {
		if (!isDrawing || !mapObject) return;

		const { draw, layer } = setupFreehandDraw({
			type: 'Polygon',
			mapRef: mapObject,
			freehand: false,
		});
		drawRef.current = { draw, layer };

		draw.on('drawend', async event => {
			const coordinates = event.feature.getGeometry().getCoordinates();
			// Keep the outer ring only; matches the payload the detection API expects.
			const ring = coordinates?.[0] ?? null;
			try {
				// Leave the drawn polygon + disabled map in place until the caller's
				// completion work (e.g. a confirm dialog) resolves.
				await handlersRef.current.onComplete?.(ring, event.feature);
			} finally {
				teardownDraw();
				restoreMap();
				setIsDrawing(false);
			}
		});

		const handleKeyUp = ({ key }) => {
			if (key === 'Escape') cancelDraw();
		};
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keyup', handleKeyUp);
			teardownDraw();
			restoreMap();
		};
		// Intentionally only re-run when a draw actually starts/stops or the map
		// instance changes. The `onComplete`/`onCancel` callbacks are read via
		// `handlersRef` (so their identity doesn't matter), and the local helpers
		// (teardownDraw/restoreMap/cancelDraw) close over stable `mapObject`/
		// `dispatch`; adding them would needlessly tear down and rebuild the draw.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDrawing, mapObject]);

	return { isDrawing, startDraw, cancelDraw };
};
